/**
 * A report kind's options as a form, rendered from the JSON Schema the backend declares (RP5).
 *
 * Only what our own schemas use is supported: objects, booleans, numbers, strings (a choice when
 * they declare an `enum`) and arrays of objects. Labels come from
 * `reports:settings.fields.<kind>.<path>` (`…._` for an object, an array or a choice, whose values
 * are labelled `….<value>`); array indices are left out of the key, so every row of a table
 * shares its column labels.
 */
import { ActionIcon, Button, Fieldset, NumberInput, Select, Stack, Switch, Table, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { STATUS_COLORS } from '@/theme/colors';
import { defaultsOf, getAt, pathKey, setAt, type Path } from './schemaPath';

export interface JsonSchema {
  type?: 'object' | 'boolean' | 'number' | 'integer' | 'string' | 'array';
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema;
  default?: unknown;
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  minLength?: number;
  maxLength?: number;
  required?: string[];
  enum?: string[];
}

export interface SchemaFormProps {
  kind: string;
  schema: JsonSchema;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  errors?: Record<string, string>;
}

interface FieldProps {
  schema: JsonSchema;
  path: Path;
  root: Record<string, unknown>;
  set: (path: Path, next: unknown) => void;
  label: (path: Path, schema: JsonSchema) => string;
  /** The label of one value of a choice. */
  choice: (path: Path, value: string) => string;
  errors: Record<string, string>;
  disabled: boolean;
  ariaLabel?: string;
}

function ErrorText({ text }: { text?: string }) {
  return text ? (
    <Text fz={12} c={STATUS_COLORS.failed.color}>
      {text}
    </Text>
  ) : null;
}

function Field({ schema, path, root, set, label, choice, errors, disabled, ariaLabel }: FieldProps) {
  const { t } = useTranslation('reports');
  const value = getAt(root, path);
  const error = errors[pathKey(path)];
  switch (schema.type) {
    case 'object': {
      const props = Object.entries(schema.properties ?? {});
      // A section that is switched off keeps its settings but greys them out.
      const off = disabled || (value as { enabled?: unknown } | undefined)?.enabled === false;
      const body = (
        <Stack gap="xs">
          {props.map(([key, sub]) => (
            <Field key={key} schema={sub} path={[...path, key]} root={root} set={set} label={label} choice={choice} errors={errors} disabled={key === 'enabled' ? disabled : off} />
          ))}
          <ErrorText text={error} />
        </Stack>
      );
      return path.length === 0 ? body : <Fieldset legend={label(path, schema)}>{body}</Fieldset>;
    }
    case 'boolean':
      return <Switch aria-label={ariaLabel} label={label(path, schema)} checked={value === true} disabled={disabled} onChange={(e) => set(path, e.currentTarget.checked)} error={error} />;
    case 'number':
    case 'integer':
      return (
        <NumberInput
          aria-label={ariaLabel}
          label={label(path, schema)}
          value={typeof value === 'number' ? value : ''}
          min={schema.minimum}
          max={schema.maximum}
          allowDecimal={schema.type === 'number'}
          disabled={disabled}
          error={error}
          onChange={(v) => set(path, typeof v === 'number' ? v : undefined)}
        />
      );
    case 'string':
      if (schema.enum)
        return (
          <Select
            aria-label={ariaLabel}
            label={label(path, schema)}
            data={schema.enum.map((v) => ({ value: v, label: choice(path, v) }))}
            value={typeof value === 'string' ? value : null}
            allowDeselect={false}
            disabled={disabled}
            error={error}
            onChange={(v) => v !== null && set(path, v)}
          />
        );
      return <TextInput aria-label={ariaLabel} label={label(path, schema)} value={typeof value === 'string' ? value : ''} disabled={disabled} error={error} onChange={(e) => set(path, e.currentTarget.value)} />;
    case 'array': {
      const rows = Array.isArray(value) ? value : [];
      const item = schema.items ?? {};
      const columns = Object.entries(item.properties ?? {});
      return (
        <Stack gap={4}>
          <Text fz={14} fw={600}>
            {label(path, schema)}
          </Text>
          <Table>
            <Table.Thead>
              <Table.Tr>
                {columns.map(([key, sub]) => (
                  <Table.Th key={key}>{label([...path, key], sub)}</Table.Th>
                ))}
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((_, i) => (
                <Table.Tr key={i}>
                  {columns.map(([key, sub]) => (
                    <Table.Td key={key}>
                      <Field schema={sub} path={[...path, i, key]} root={root} set={set} label={() => ''} choice={choice} errors={errors} disabled={disabled} ariaLabel={label([...path, key], sub)} />
                    </Table.Td>
                  ))}
                  <Table.Td>
                    <ActionIcon variant="subtle" aria-label={t('settings.removeRow')} disabled={disabled} onClick={() => set(path, rows.filter((__, j) => j !== i))}>
                      ×
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
              {rows.map((_, i) =>
                errors[pathKey([...path, i])] ? (
                  <Table.Tr key={`e${i}`}>
                    <Table.Td colSpan={columns.length + 1}>
                      <ErrorText text={errors[pathKey([...path, i])]} />
                    </Table.Td>
                  </Table.Tr>
                ) : null,
              )}
            </Table.Tbody>
          </Table>
          <Button size="xs" variant="default" disabled={disabled} onClick={() => set(path, [...rows, defaultsOf(item)])}>
            {t('settings.addRow')}
          </Button>
          <ErrorText text={error} />
        </Stack>
      );
    }
    default:
      throw new Error(`SchemaForm: unsupported schema at ${pathKey(path)}`);
  }
}

export function SchemaForm({ kind, schema, value, onChange, errors = {} }: SchemaFormProps) {
  const { t } = useTranslation('reports');
  // An object or array is also the parent of its fields' keys, so its own label sits under `_`.
  const keyOf = (path: Path) => [kind, ...path.filter((p) => typeof p === 'string')].join('.');
  const label = (path: Path, at: JsonSchema) => t(`settings.fields.${keyOf(path)}${at.type === 'object' || at.type === 'array' || at.enum ? '._' : ''}`);
  const choice = (path: Path, value: string) => t(`settings.fields.${keyOf(path)}.${value}`, { defaultValue: value });
  const set = (path: Path, next: unknown) => onChange(setAt(value, path, next) as Record<string, unknown>);
  return <Field schema={schema} path={[]} root={value} set={set} label={label} choice={choice} errors={errors} disabled={false} />;
}
